import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, SendHorizonal, Loader2 } from "lucide-react";
import { streamChat } from "../lib/api";
import { EASE } from "./Reveal";

const SUGGESTIONS = [
  "How do I reduce my unsold fashion inventory?",
  "What is Sundeepa's background with Zara and H&M?",
  "How is the Indian Colour Trend-Book different from Pantone?",
  "What's included in the Seasonal Advisory package?",
];

const WELCOME =
  "Hello — I'm Studio SB, Sundeepa's strategy assistant. Ask me about her services, her Zara & H&M years, or how to fix a collection that isn't selling.";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", text: WELCOME }]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionRef = useRef(`s-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "" }]);
    setStreaming(true);
    let i = 0;
    await streamChat({
      sessionId: sessionRef.current,
      message: msg,
      onDelta: (delta) => {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", text: (copy[copy.length - 1]?.text || "") + delta };
          return copy;
        });
      },
      onDone: () => setStreaming(false),
      onError: (err) => {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = {
            role: "assistant",
            text: i === 0 ? err : (copy[copy.length - 1]?.text || ""),
          };
          i += 1;
          return copy;
        });
        setStreaming(false);
      },
    });
  };

  return (
    <>
      <motion.button
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.6, duration: 0.6, ease: EASE }}
        onClick={() => setOpen((o) => !o)}
        data-testid="ai-bot-toggle-btn"
        aria-label={open ? "Close assistant" : "Open assistant"}
        className="fixed bottom-5 right-5 sm:bottom-7 sm:right-7 z-[70] w-14 h-14 rounded-full bg-[#1d1d1f] text-white shadow-[0_12px_32px_rgba(0,0,0,0.25)] flex items-center justify-center hover:bg-[#0066cc] transition-colors active:scale-95"
      >
        {open ? <X size={22} /> : <Sparkles size={22} />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.45, ease: EASE }}
            className="fixed bottom-20 right-4 sm:bottom-24 sm:right-7 z-[70] w-[calc(100vw-2rem)] max-w-[390px] h-[560px] max-h-[calc(100vh-7rem)] rounded-3xl overflow-hidden glass-nav border border-black/[0.08] shadow-[0_30px_70px_rgba(0,0,0,0.25)] flex flex-col"
            data-testid="ai-bot-panel"
          >
            <div className="px-5 py-4 bg-[#1d1d1f] text-white flex items-center gap-3">
              <span className="w-9 h-9 rounded-full bg-white/10 border border-white/15 flex items-center justify-center">
                <Sparkles size={16} className="text-[#d4af37]" />
              </span>
              <div>
                <p className="text-sm font-semibold leading-tight">Studio SB</p>
                <p className="text-[11px] text-white/60">Fashion strategy assistant</p>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto thin-scroll px-4 py-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-message-${m.role}`}>
                  <div
                    className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-[14px] leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-[#0066cc] text-white rounded-br-md"
                        : "bg-white border border-black/[0.06] text-[#1d1d1f] rounded-bl-md shadow-sm"
                    } ${m.role === "assistant" && !m.text ? "px-3" : ""}`}
                  >
                    {m.text ? m.text : <Loader2 size={14} className="animate-spin text-[#86868b]" />}
                  </div>
                </div>
              ))}
              {messages.length <= 1 && (
                <div className="pt-2 flex flex-col gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      data-testid="ai-bot-suggestion"
                      onClick={() => send(s)}
                      className="text-left text-[13px] px-4 py-2.5 rounded-2xl border border-[#0066cc]/25 bg-white/70 text-[#0066cc] font-medium hover:bg-[#0066cc]/[0.06] transition-all"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="p-3 border-t border-black/[0.06] bg-white/60 flex items-center gap-2"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about your collection..."
                data-testid="ai-bot-input"
                className="flex-1 px-4 py-3 rounded-full bg-white border border-[#d2d2d7] text-[14px] outline-none focus:border-[#0066cc] transition-colors"
              />
              <button
                type="submit" disabled={streaming || !input.trim()}
                data-testid="ai-bot-send-btn"
                aria-label="Send message"
                className="w-11 h-11 rounded-full bg-[#1d1d1f] text-white flex items-center justify-center hover:bg-[#0066cc] transition-colors disabled:opacity-40 shrink-0"
              >
                {streaming ? <Loader2 size={16} className="animate-spin" /> : <SendHorizonal size={16} />}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
